import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';

import {
  environmentQueryOptions,
  hardnessDomainByBlastQueryOptions,
  infrastructureQueryOptions,
  unsurveyedQueryOptions,
} from '~api/queries';
import type {
  EnvironmentResponse,
  HardnessDomainResponse,
  InfrastructureResponse,
  UnsurveyedResponse,
} from '~api/services';
import type { LayerVisibility } from '~libs';
import { useLayerVisibility } from '~libs';

/* -------------------------------------------------------------------------- */
/*                                    Types                                   */
/* -------------------------------------------------------------------------- */

type UseCanvasLayersOptions = {
  /** The blast name to query layer data for */
  blastName: string | undefined;
};

type UseCanvasLayersResult = {
  // Data
  hardnessDomains: HardnessDomainResponse | undefined;
  unsurveyedSites: UnsurveyedResponse | undefined;
  infrastructureSites: InfrastructureResponse | undefined;
  environmentSites: EnvironmentResponse | undefined;
  // Loading states
  loadingHardnessDomains: boolean;
  loadingUnsurveyedSites: boolean;
  loadingInfrastructureSites: boolean;
  loadingEnvironmentSites: boolean;
  // Error states
  isHardnessDomainsError: boolean;
  isUnsurveyedSitesError: boolean;
  isInfrastructureSitesError: boolean;
  isEnvironmentSitesError: boolean;
  hardnessDomainsError: Error | null;
  unsurveyedSitesError: Error | null;
  infrastructureSitesError: Error | null;
  environmentSitesError: Error | null;
  // Actions
  refetchHardnessDomains: () => void;
  // Derived presence flags
  haveUnsurveyedSites: boolean;
  haveInfrastructureSites: boolean;
  haveEnvironmentSites: boolean;
  // Visibility controls
  hardnessZoneVisibility: LayerVisibility;
  unsurveyedVisibility: LayerVisibility;
  infrastructureVisibility: LayerVisibility;
  environmentVisibility: LayerVisibility;
};

/* -------------------------------------------------------------------------- */
/*                                  Main Hook                                 */
/* -------------------------------------------------------------------------- */

/**
 * Aggregates canvas layer data fetching, visibility state, and presence flags for OptimiserDrillHoleCanvas.
 *
 * **Why a single hook?** The layers share a cross-cutting concern: each must
 * auto-hide when its query returns an empty feature set. Centralising that in one
 * `useEffect` keeps the relationship explicit and the component focused on rendering.
 *
 * **Impact on `OptimiserDrillHoleCanvas`** — layer data is destructured separately
 * from `...layerControls` so the spread can be passed straight to `CanvasLayerControls`,
 * which is what keeps the component's cyclomatic complexity within Biome's limit of 15.
 *
 * **If this hook grows too large** — migrate the flat return shape to a grouped structure:
 * `{ data, isLoading, isError, error, hasFeatures, visibility }` per layer. Hold off until
 * a fifth layer is actually added, as the refactor touches every call site.
 *
 * !Note: Drill hole data is excluded — it derives from the optimisation prop, not from
 * a blast-name query, so it lives in `useDrillHoleData` instead.
 */
const useCanvasLayers = ({ blastName }: UseCanvasLayersOptions): UseCanvasLayersResult => {
  const hardnessZoneVisibility = useLayerVisibility({ visible: false, opacity: 1 });
  const unsurveyedVisibility = useLayerVisibility({ visible: true, opacity: 1 });
  const infrastructureVisibility = useLayerVisibility({ visible: true, opacity: 1 });
  const environmentVisibility = useLayerVisibility({ visible: true, opacity: 1 });

  const {
    data: hardnessDomains,
    isLoading: loadingHardnessDomains,
    isError: isHardnessDomainsError,
    error: hardnessDomainsError,
    refetch: refetchHardnessDomains,
  } = useQuery({
    ...hardnessDomainByBlastQueryOptions(blastName as string),
    enabled: !!blastName,
  });

  const {
    data: unsurveyedSites,
    isLoading: loadingUnsurveyedSites,
    isError: isUnsurveyedSitesError,
    error: unsurveyedSitesError,
  } = useQuery({
    ...unsurveyedQueryOptions({ blast_name: blastName as string }),
    enabled: !!blastName,
  });

  const {
    data: infrastructureSites,
    isLoading: loadingInfrastructureSites,
    isError: isInfrastructureSitesError,
    error: infrastructureSitesError,
  } = useQuery({
    ...infrastructureQueryOptions({ blast_name: blastName as string }),
    enabled: !!blastName,
  });

  const {
    data: environmentSites,
    isLoading: loadingEnvironmentSites,
    isError: isEnvironmentSitesError,
    error: environmentSitesError,
  } = useQuery({
    ...environmentQueryOptions({ blast_name: blastName as string }),
    enabled: !!blastName,
  });

  const haveUnsurveyedSites = !!unsurveyedSites?.features?.length;
  const haveInfrastructureSites = !!infrastructureSites?.features?.length;
  const haveEnvironmentSites = !!environmentSites?.features?.length;

  const { setVisible: setUnsurveyedVisible } = unsurveyedVisibility;
  const { setVisible: setInfrastructureVisible } = infrastructureVisibility;
  const { setVisible: setEnvironmentVisible } = environmentVisibility;

  /**
   * Auto-hide layers when their queries return an empty result set.
   * The layer control will show an informational error message in place of the toggle.
   */
  useEffect(() => {
    setUnsurveyedVisible(haveUnsurveyedSites);
    setInfrastructureVisible(haveInfrastructureSites);
    setEnvironmentVisible(haveEnvironmentSites);
  }, [
    haveUnsurveyedSites,
    setUnsurveyedVisible,
    haveInfrastructureSites,
    setInfrastructureVisible,
    haveEnvironmentSites,
    setEnvironmentVisible,
  ]);

  /**
   * We expose loading/error states and presence flags alongside the raw data so that
   * `CanvasLayerControls` can distinguish three distinct UI states per layer:
   *   1. Still loading    → show a spinner in the layer toggle
   *   2. Loaded, no data  → disable the toggle and show "no features found" hint
   *   3. Query failed     → disable the toggle and show the API error message
   *
   * Returning only the data and visibility objects would collapse cases 2 and 3
   * into a single "layer unavailable" state, losing the actionable detail.
   */
  return {
    hardnessDomains,
    unsurveyedSites,
    infrastructureSites,
    environmentSites,
    loadingHardnessDomains,
    loadingUnsurveyedSites,
    loadingInfrastructureSites,
    loadingEnvironmentSites,
    isHardnessDomainsError,
    isUnsurveyedSitesError,
    isInfrastructureSitesError,
    isEnvironmentSitesError,
    hardnessDomainsError,
    unsurveyedSitesError,
    infrastructureSitesError,
    environmentSitesError,
    refetchHardnessDomains,
    haveUnsurveyedSites,
    haveInfrastructureSites,
    haveEnvironmentSites,
    hardnessZoneVisibility,
    unsurveyedVisibility,
    infrastructureVisibility,
    environmentVisibility,
  };
};

export { useCanvasLayers };
